<template>
  <div class="m7-console" :class="[`m7-console--${size}`, { 'is-collapsed': collapsed }]">
    <!-- Compact pill: small enough to park beside a chord chart while playing. -->
    <div v-if="collapsed" class="m7-console__pill">
      <button
        type="button"
        class="m7-console__pill-play"
        :class="{ 'is-playing': engine.isPlaying.value }"
        :aria-label="playLabel"
        @click="engine.toggle()"
      >
        <v-icon :icon="engine.isPlaying.value ? '$stop' : '$play'" size="16" />
      </button>

      <MetronomeBeatDots
        :accents="store.accents"
        :current-beat="engine.currentBeat.value"
        :beat-tick="engine.beatTick.value"
        :playing="engine.isPlaying.value"
        size="sm"
      />

      <span class="m7-console__pill-bpm">{{ store.settings.bpm }}</span>
    </div>

    <template v-else>
      <header class="m7-console__header">
        <v-btn
          :icon="view === 'main' ? '$tuning' : '$arrowLeft'"
          variant="text"
          size="small"
          :aria-label="view === 'main' ? $t('metronome.modifySound') : $t('metronome.back')"
          @click="view === 'main' ? (view = 'tune') : (view = 'main')"
        />

        <button
          type="button"
          class="m7-console__title"
          :disabled="view !== 'main'"
          @click="view = view === 'main' ? 'time' : view"
        >
          {{ view === 'main' ? store.settings.timeSignature : $t(`metronome.views.${view}`) }}
        </button>

        <v-btn icon variant="text" size="small" :aria-label="$t('metronome.menu')">
          <v-icon icon="$menu" />
          <v-menu activator="parent" location="bottom end">
            <v-list density="compact">
              <v-list-item
                v-for="item in menuItems"
                :key="item.view"
                :active="view === item.view"
                @click="view = item.view"
              >
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
              <v-divider class="my-1" />
              <!--
                A checkbox row rather than a v-switch: a switch's track and
                label overflow the menu's width and get clipped. The checkbox
                is display-only (pointer-events: none) so the row click is the
                single toggle path and the two can't fight over one tap.
              -->
              <v-list-item
                role="menuitemcheckbox"
                :aria-checked="store.settings.syncEnabled"
                :title="$t('metronome.sync')"
                @click="setSync(!store.settings.syncEnabled)"
              >
                <template #append>
                  <v-checkbox-btn
                    :model-value="store.settings.syncEnabled"
                    color="primary"
                    density="compact"
                    tabindex="-1"
                    class="m7-console__check"
                  />
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-btn>
      </header>

      <div v-if="view === 'main'" class="m7-console__main">
        <MetronomeBeatDots
          :accents="store.accents"
          :current-beat="engine.currentBeat.value"
          :beat-tick="engine.beatTick.value"
          :playing="engine.isPlaying.value"
          editable
          class="mb-3"
          :aria-label="$t('metronome.accentPattern')"
          @cycle="store.cycleAccentAt($event)"
        />

        <div class="m7-console__tempo">
          <button
            type="button"
            class="m7-console__nudge"
            :aria-label="$t('metronome.slower')"
            @pointerdown="startNudge(-1)"
            @pointerup="stopNudge"
            @pointerleave="stopNudge"
            @pointercancel="stopNudge"
          >
            <v-icon icon="$minus" size="20" />
          </button>

          <div class="m7-console__bpm">
            <span class="m7-console__bpm-value">{{ store.settings.bpm }}</span>
            <span class="m7-console__bpm-unit">{{ $t('metronome.bpm') }}</span>
          </div>

          <button
            type="button"
            class="m7-console__nudge"
            :aria-label="$t('metronome.faster')"
            @pointerdown="startNudge(1)"
            @pointerup="stopNudge"
            @pointerleave="stopNudge"
            @pointercancel="stopNudge"
          >
            <v-icon icon="$plus" size="20" />
          </button>
        </div>

        <MetronomeDial
          :model-value="store.settings.bpm"
          :min="MIN_BPM"
          :max="MAX_BPM"
          :size="dialSize"
          :tap-label="$t('metronome.tap')"
          :tapping="isTapping"
          @update:model-value="store.setBpm($event)"
          @tap="handleTap"
        />

        <div class="m7-console__transport">
          <v-btn
            :icon="store.settings.muted ? '$volumeOff' : '$volumeHigh'"
            variant="text"
            size="small"
            :aria-label="$t('metronome.sound')"
            @click="store.update('muted', !store.settings.muted)"
          />

          <button
            type="button"
            class="m7-console__play"
            :class="{ 'is-playing': engine.isPlaying.value }"
            :aria-label="playLabel"
            @click="engine.toggle()"
          >
            <v-icon :icon="engine.isPlaying.value ? '$stop' : '$play'" size="22" />
          </button>

          <v-btn
            icon="$musicNote"
            variant="text"
            size="small"
            :aria-label="$t('metronome.clickSound')"
            @click="view = 'sound'"
          />
        </div>

        <p class="m7-console__status">{{ statusText }}</p>
      </div>

      <MetronomeTimePanel v-else-if="view === 'time'" />
      <MetronomeSoundPanel v-else-if="view === 'sound'" @preview="engine.preview($event)" />
      <MetronomeTunePanel v-else-if="view === 'tune'" @preview="engine.preview()" />
    </template>
  </div>
</template>

<script setup>
import { MAX_BPM, MIN_BPM } from '~/utils/metronomeMeter'

const props = defineProps({
  /** 'dock' is the in-app floating panel, 'page' the standalone public page. */
  size: { type: String, default: 'dock' },
  collapsed: { type: Boolean, default: false },
})

const { t } = useI18n()
const store = useMetronomeStore()

const view = ref('main')

const engine = useMetronomeEngine(() => ({
  bpm: store.settings.bpm,
  timeSignature: store.settings.timeSignature,
  compound: store.settings.compound,
  subdivision: store.settings.subdivision,
  accents: store.accents,
  voice: store.settings.voice,
  volume: store.settings.volume,
  pitch: store.settings.pitch,
  eq: store.settings.eq,
  muted: store.settings.muted,
  syncEnabled: store.settings.syncEnabled,
}))

const dialSize = computed(() => (props.size === 'page' ? 260 : 190))

const playLabel = computed(() => (engine.isPlaying.value ? t('metronome.stop') : t('metronome.play')))

const menuItems = computed(() => [
  { view: 'main', title: t('metronome.views.main') },
  { view: 'time', title: t('metronome.views.time') },
  { view: 'sound', title: t('metronome.views.sound') },
  { view: 'tune', title: t('metronome.views.tune') },
])

const statusText = computed(() => {
  if (engine.isPreparing.value) return t('metronome.waitingForDownbeat')
  if (!store.settings.syncEnabled) return ''
  if (engine.clockStatus.value === 'degraded') return t('metronome.syncDegraded')
  if (engine.clockStatus.value === 'ready') {
    return t('metronome.syncReady', { ms: engine.clockAccuracyMs.value })
  }
  return t('metronome.syncing')
})

// Mix changes have to be pushed into the audio graph; they aren't read per-tick.
watch(
  () => [store.settings.muted, store.settings.volume, store.settings.eq],
  () => engine.applyMix(),
  { deep: true }
)

// Anything in the app can ask the metronome to stop (e.g. a live-view song change).
watch(
  () => store.stopRequests,
  () => engine.stop()
)

function setSync(value) {
  store.update('syncEnabled', value)
  // Changing the grid mid-play would drift; restart cleanly on the new basis.
  if (engine.isPlaying.value || engine.isPreparing.value) {
    engine.stop()
    void engine.start()
  } else if (value) {
    // Warm the offset up front so the first play doesn't pay for the handshake.
    void useServerClock().ensureSynced()
  }
}

// Tap tempo, shared with the song form's BPM field.
const { tap, bpm: tappedBpm, isActive: isTapping } = useTempoTap()

function handleTap() {
  tap()
  if (tappedBpm.value) store.setBpm(tappedBpm.value)
}

// Hold either nudge button to run the tempo up or down.
let nudgeTimer = null

function startNudge(delta) {
  store.nudgeBpm(delta)
  stopNudge()
  nudgeTimer = setInterval(() => store.nudgeBpm(delta), 110)
}

function stopNudge() {
  if (nudgeTimer) {
    clearInterval(nudgeTimer)
    nudgeTimer = null
  }
}

onBeforeUnmount(stopNudge)

defineExpose({ engine })
</script>

<style scoped>
.m7-console {
  /* Scoped design tokens. They map onto the app's theme, so the console
     follows light/dark for free while keeping its own soft-UI treatment. */
  --m7-surface: rgb(var(--v-theme-surface));
  --m7-accent: rgb(var(--v-theme-primary));
  --m7-accent-glow: rgba(var(--v-theme-primary), 0.4);
  --m7-tick: rgba(var(--v-theme-on-surface), 0.16);
  --m7-muted: rgba(var(--v-theme-on-surface), 0.5);
  --m7-shadow: rgba(var(--v-theme-on-surface), 0.09);
  --m7-knob: linear-gradient(160deg, rgba(255, 255, 255, 0.9), rgba(var(--v-theme-on-surface), 0.05));
  --m7-knob-highlight: rgba(255, 255, 255, 0.85);

  background: var(--m7-surface);
  border-radius: 22px;
  padding: 0.9rem 1rem 1.25rem;
  color: rgb(var(--v-theme-on-surface));
}

:global(.v-theme--dark) .m7-console {
  --m7-knob: linear-gradient(160deg, rgba(255, 255, 255, 0.08), rgba(0, 0, 0, 0.28));
  --m7-knob-highlight: rgba(255, 255, 255, 0.12);
  --m7-shadow: rgba(0, 0, 0, 0.45);
}

.m7-console--page {
  padding: 1.75rem 1.5rem 2.25rem;
  box-shadow: 0 18px 48px var(--m7-shadow);
}

.m7-console.is-collapsed {
  padding: 0.25rem 0.5rem;
}

/* Collapsed pill */
.m7-console__pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.m7-console__pill-bpm {
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  color: var(--m7-muted);
  min-width: 2ch;
}

.m7-console__pill-play {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid var(--m7-tick);
  background: transparent;
  color: var(--m7-accent);
  flex: none;
}

.m7-console__pill-play.is-playing {
  background: var(--m7-accent);
  border-color: var(--m7-accent);
  color: rgb(var(--v-theme-on-primary));
}

/* Header */
.m7-console__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.m7-console__title {
  flex: 1;
  background: none;
  border: none;
  font-size: 0.95rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: inherit;
  cursor: pointer;
}

.m7-console__title:disabled {
  cursor: default;
}

.m7-console__main {
  text-align: center;
}

/* Tempo hero */
.m7-console__tempo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.m7-console__nudge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--m7-muted);
  touch-action: none;
}

.m7-console__nudge:hover {
  color: var(--m7-accent);
}

.m7-console__bpm {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 4.5rem;
}

.m7-console__bpm-value {
  font-size: 3rem;
  font-weight: 200;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.m7-console--page .m7-console__bpm-value {
  font-size: 4rem;
}

.m7-console__bpm-unit {
  font-size: 0.6rem;
  letter-spacing: 0.24em;
  color: var(--m7-muted);
}

/* Transport */
.m7-console__transport {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.75rem;
}

.m7-console__play {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 1px solid var(--m7-tick);
  background: transparent;
  color: var(--m7-accent);
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.m7-console__play.is-playing {
  background: var(--m7-accent);
  border-color: var(--m7-accent);
  color: rgb(var(--v-theme-on-primary));
}

.m7-console__check {
  pointer-events: none;
}

.m7-console__status {
  min-height: 1.1rem;
  margin-top: 0.5rem;
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  color: var(--m7-muted);
}
</style>
